/**
 * CreateActionHandlerTask - таск для відкриття карточки створення нового ресурсу
 *
 * Використовує нову систему карточок через OpenResourceCardTask.
 * Карточка відкривається в Drawer через ResourceCardRenderer.
 *
 * На відміну від view/edit режимів, для create не потрібно завантажувати дані з API.
 * Початкові значення передаються через параметр initialValues.
 * ResourceDetail в режимі create автоматично налаштує форму для створення.
 *
 * @param {Object} params - Параметри
 * @param {Object} params.initialValues - Початкові значення для нового елементу
 * @param {Object} params.crudContext - Контекст CRUD ресурсу
 */

import type { IOpenResourceCardResult } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'

/**
 * Параметри для CreateActionHandlerTask
 */
interface ICreateActionHandlerParams {
    /** Початкові значення для форми */
    initialValues?: Record<string, any>
    /** Контекст CRUD ресурсу */
    crudContext: any // CrudResourceStore з mobx
    /** Додаткові параметри для OpenResourceCardTask (дозволяє перезаписати onClose, drawer, displayMode тощо) */
    openResourceCardParams?: Partial<Parameters<typeof OpenResourceCardTask>[0]>
}

export const CreateActionHandlerTask = async ({
    initialValues,
    crudContext,
    openResourceCardParams,
}: ICreateActionHandlerParams): Promise<IOpenResourceCardResult | undefined> => {
    initialValues ??= {}

    // Викликаємо подію перед відкриттям для можливості скасування
    const eventResults = await crudContext.eventManager.emitAsync('onBeforeCreate', {
        initialValues,
        crudContext,
    })

    // Якщо хоча б один обробник повернув false - скасовуємо відкриття
    if (eventResults && eventResults.some((result: any) => result === false)) {
        return
    }
    
    // Визначаємо режим відображення:
    // 1. Пріоритет - openResourceCardParams.displayMode
    // 2. modeConfig.create.displayMode
    // 3. Загальний detailConfig.mode з конфігурації ресурсу
    // 4. За замовчуванням - drawer
    const displayMode = 
        openResourceCardParams?.displayMode ||
        crudContext.resource?.detailConfig?.modeConfig?.create?.displayMode ||
        crudContext.resource?.detailConfig?.mode || 
        'drawer'
    
    // Визначаємо disableAutoRedirectAfterCreate
    const disableAutoRedirectAfterCreate = 
        crudContext.resource?.detailConfig?.modeConfig?.create?.disableAutoRedirectAfterCreate ?? 
        crudContext.resource?.disableAutoRedirectAfterCreate ??
        false
    
    // Дефолтні параметри
    const defaultParams = {
        resource: crudContext.resource,
        mode: 'create' as const,
        displayMode: displayMode,
        initialValues,
        listCrudContext: crudContext, // Передаємо поточний контекст списку для перезавантаження
        props: {
            // Передаємо disableAutoRedirectAfterCreate з конфігурації ресурсу
            disableAutoRedirectAfterCreate,
        },
        drawer: {
            title: crudContext.drawer?.createTitle || `Створити: ${crudContext.resource?.title || ''}`,
            width: crudContext.drawer?.width || 500,
        },
        onClose: () => {
            // Перезавантажуємо список при закритті — зміни можуть бути внесені не тільки через кнопку збереження
            // Використовуємо listContext напряму, щоб уникнути відкриття картки через ReloadActionHandlerTask
            if (crudContext.listContext?.actions?.load) {
                crudContext.listContext.actions.load(true)
            }

            // Викликаємо події при закритті
            crudContext.eventManager.emit('onCloseDrawer', {
                crudContext,
            })
        },
        onModeChange: (mode: string) => {
            // Обробка зміни режиму
            crudContext.eventManager.emit('onModeChange', {
                mode,
                crudContext,
            })
        },
    }
    
    // Об'єднуємо дефолтні параметри з переданими через openResourceCardParams
    // openResourceCardParams має вищий пріоритет
    const result = OpenResourceCardTask({
        ...defaultParams,
        ...openResourceCardParams,
        // Drawer об'єднуємо окремо для можливості часткової перезапису
        drawer: {
            ...defaultParams.drawer,
            ...openResourceCardParams?.drawer,
        },
        // Props об'єднуємо окремо
        props: {
            ...defaultParams.props,
            ...openResourceCardParams?.props,
        },
    })

    // Викликаємо подію після відкриття
    crudContext.eventManager.emit('onOpenCreateCard', {
        initialValues,
        cardId: result.cardId,
        crudContext,
    })

    return result
}
